import {
  API_BASE_URL,
  BUSINESS_UNIT_ID,
  TENANT_ID,
  apiUrl,
} from "@/config/api";

const CS_FETCH_HEADERS: Record<string, string> = {
  Accept: "application/json",
  "Content-Type": "application/json",
  "x-tenant-id": TENANT_ID,
  "x-business-unit-id": BUSINESS_UNIT_ID,
};

export type PetrominCity = {
  id: string;
  name: string;
  native?: string;
  latitude?: string;
  longitude?: string;
};

type PetrominCitiesApiResponse = {
  success?: boolean;
  result?: {
    cities?: Array<{
      id?: string;
      name?: string;
      native?: string;
      latitude?: string;
      longitude?: string;
    }>;
  };
};

function csClientBaseUrl(): string {
  return (
    process.env.NEXT_PUBLIC_NODE_BACKEND_API_URL?.trim() ||
    API_BASE_URL
  ).replace(/\/+$/, "");
}

function normalizeCity(raw: {
  id?: string;
  name?: string;
  native?: string;
  latitude?: string;
  longitude?: string;
}): PetrominCity | null {
  const id = raw.id?.trim();
  const name = raw.name?.trim();
  if (!id || !name) return null;
  return {
    id,
    name,
    native: raw.native?.trim() || undefined,
    latitude: raw.latitude?.trim() || undefined,
    longitude: raw.longitude?.trim() || undefined,
  };
}

function unwrapCities(json: PetrominCitiesApiResponse): PetrominCity[] {
  const list = json.result?.cities;
  if (!Array.isArray(list)) return [];
  return list
    .map((row) => normalizeCity(row))
    .filter((city): city is PetrominCity => city != null);
}

function buildCitiesUrl(base: string, search?: string): string {
  const params = new URLSearchParams();
  const q = search?.trim();
  if (q) params.set("search", q);
  const query = params.toString();
  return `${base}/cities${query ? `?${query}` : ""}`;
}

/** Client-side `GET /cities` on the Petromin CS host. */
export async function fetchPetrominCities(search?: string): Promise<PetrominCity[]> {
  const res = await fetch(buildCitiesUrl(csClientBaseUrl(), search), {
    headers: CS_FETCH_HEADERS,
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`Cities ${res.status}`);
  }
  const json = (await res.json()) as PetrominCitiesApiResponse;
  return unwrapCities(json);
}

/** Server-side `GET /cities` (Route Handlers / RSC). */
export async function fetchPetrominCitiesServer(
  search?: string
): Promise<PetrominCity[]> {
  const params = new URLSearchParams();
  const q = search?.trim();
  if (q) params.set("search", q);
  const path = params.toString() ? `/cities?${params}` : "/cities";
  const res = await fetch(apiUrl(path), {
    headers: CS_FETCH_HEADERS,
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`Cities ${res.status}`);
  }
  const json = (await res.json()) as PetrominCitiesApiResponse;
  return unwrapCities(json);
}
